import { config } from '@app/config';
import { useAuthContext } from '@app/core/auth/authentication/context';
import { useImpersonation } from '@app/core/auth/impersonation';
import { useUiStore } from '@app/core/ui/stores/ui.store';
import { useCallback, useMemo } from 'react';
import { buildVideoEditorEditUrl } from '../lib/video-editor-edit-url';

/**
 * Returns the full URL to open an edit in the video editor (new tab), including the JWT from auth context.
 * When an admin is impersonating a user, adds auth_as=userId so the editor knows which user context to use.
 * Returns an empty string if base URL is invalid, editId is missing, or token is not available.
 */
export function useVideoEditorEditUrl(editId: string): string {
  const { token } = useAuthContext();
  const { impersonatedUser } = useImpersonation();
  const themeMode = useUiStore((state) => state.themeMode);

  return useMemo(() => {
    if (!token || !editId) return '';
    return buildVideoEditorEditUrl({
      baseUrl: config.videoEditorUrl,
      editId,
      jwt: token,
      theme: themeMode,
      authAsUserId: impersonatedUser?.id,
    });
  }, [editId, token, themeMode, impersonatedUser?.id]);
}

/**
 * Returns a function that sends the current window to the video editor for a given edit id.
 * Uses the same URL and auth rules as {@link useVideoEditorEditUrl}. No-op when token or editId is missing.
 */
export function useRedirectToVideoEditorEdit() {
  const { token } = useAuthContext();
  const { impersonatedUser } = useImpersonation();
  const themeMode = useUiStore((state) => state.themeMode);

  return useCallback(
    (editId: string) => {
      if (!token || !editId) return;
      window.location.assign(
        buildVideoEditorEditUrl({
          baseUrl: config.videoEditorUrl,
          editId,
          jwt: token,
          theme: themeMode,
          authAsUserId: impersonatedUser?.id,
        }),
      );
    },
    [token, themeMode, impersonatedUser?.id],
  );
}
